import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, Save, Eye, Sparkles, Package, TrendingUp, Wallet, Clock, Pencil, ArrowRight, AlertTriangle, Lock, Trash2, History, Plus } from "lucide-react";
import NationalityCombobox from "@/components/forms/NationalityCombobox";
import PhoneInput from "@/components/forms/PhoneInput";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { MEDICAL_QUESTIONS, COSMETIC_INGREDIENTS } from "@/lib/medicalQuestionnaire";
import { markCustomerViewed } from "@/lib/customerViewLog";
import { clientSeriesValidity, VALIDITY_TONE_CLASS } from "@/lib/seriesValidity";
import GuestPreferencesTab, { hydrateGuestPreferences } from "./GuestPreferences";
import AIBriefingModal from "@/components/AIBriefingModal";
import AttachmentThumbnails from "@/components/medical/AttachmentThumbnails";
import MedicalRecordDialog from "@/pages/staff/branch/calendar/MedicalRecordDialog";
import LongevityTab from "./customer/LongevityTab";
import GoalTab from "./goal/GoalTab";
import { useAuth } from "@/lib/auth";

// Brand → Customers → Review — a full-page profile with two tabs:
//   • Summary  visits history (chronological, most recent first) + recent orders.
//   • Details  editable contact card (first/last name, email, whatsapp,
//              nationality, birthday, gender).
// Notes / Appointments / Orders / Photos tabs were intentionally skipped per
// the operator's request.
export default function BrandCustomerDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Longevity visibility — any logged-in staff account can see the
  // Longevity tab + panel (Feb 2026 visibility fix). The whitelist
  // remains for guests only. We key off the staff session id so
  // operator accounts without an email (e.g. the seeded `admin`
  // user) still unlock the tab.
  const { staff } = useAuth();
  const longevityUnlocked = Boolean(staff?.id);
  // When the operator navigated here from the branch calendar via "See
  // Profile", `location.state.backTo` carries the calendar URL + label so
  // the "Back" link at the top swaps out for one that returns to the exact
  // branch/day they came from. Falls back to the customers list otherwise.
  const backTo = location.state?.backTo || { path: "/staff/brand/customers", label: "Back to customers" };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", whatsapp: "",
    nationality: "", birthday: "", gender: "",
    referred_by_customer_id: "",
    email_transactional_opt_in: true,
    email_promotional_opt_in: true,
    booking_alert: "",
    payment_alert: "",
    // Pre-Treatment Medical Questionnaire — separate free-text field for
    // medication allergies, a checkbox set for cosmetic-ingredient allergies,
    // a free-text field for currently-taken meds, and a per-question yes/no +
    // note map for the 19-row questionnaire.
    medication_allergies: "",
    cosmetic_allergies: [],
    current_medications: "",
    medical_questionnaire: {},
    guest_preferences: [],
  });
  const [referrer, setReferrer] = useState(null);           // hydrated on load
  const [referrerQuery, setReferrerQuery] = useState("");
  const [referrerResults, setReferrerResults] = useState([]);
  // AI Briefing modal — opens on the "AI Briefing" header button.
  const [briefingOpen, setBriefingOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, bs] = await Promise.all([
        staffApi.get(`/customers/${cid}`),
        staffApi.get("/branches"),
      ]);
      setData(r.data);
      setBranches(bs.data || []);
      const c = r.data.customer;
      setForm({
        first_name: c.first_name || (c.full_name || "").split(" ")[0] || "",
        last_name: c.last_name || (c.full_name || "").split(" ").slice(1).join(" ") || "",
        email: c.email || "",
        whatsapp: c.whatsapp || "",
        nationality: c.nationality || "",
        birthday: c.birthday || "",
        gender: c.gender || "",
        referred_by_customer_id: c.referred_by_customer_id || "",
        // Default opted-in when the profile predates the toggles (legacy rows).
        email_transactional_opt_in: c.email_transactional_opt_in !== false,
        email_promotional_opt_in: c.email_promotional_opt_in !== false,
        booking_alert: c.booking_alert || "",
        payment_alert: c.payment_alert || "",
        medication_allergies: c.medication_allergies || "",
        cosmetic_allergies: Array.isArray(c.cosmetic_allergies) ? c.cosmetic_allergies : [],
        current_medications: c.current_medications || "",
        medical_questionnaire: (c.medical_questionnaire && typeof c.medical_questionnaire === "object") ? c.medical_questionnaire : {},
        // Hydrate the new Guest Preferences list. When it's empty but the
        // legacy `booking_alert` / `payment_alert` strings hold content, seed
        // one preference per string so operators don't lose their old popup
        // on the first open — saving from the new tab will persist the list.
        guest_preferences: hydrateGuestPreferences(c),
      });
      // Hydrate the referrer chip so the operator sees who the credit went to.
      if (c.referred_by_customer_id) {
        try {
          const rr = await staffApi.get(`/customers/${c.referred_by_customer_id}`);
          setReferrer(rr.data.customer);
        } catch { setReferrer(null); }
      } else setReferrer(null);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/staff/brand/customers");
    } finally {
      setLoading(false);
    }
  }, [cid, navigate]);
  useEffect(() => { load(); }, [load]);

  // Live-sync (Feb 2026): the front-desk Check-In dialog broadcasts a
  // window event when the guest hits Finish on the kiosk / phone,
  // carrying their customer_id. If this profile view is watching the
  // same guest, refetch so freshly-typed email / WhatsApp / nationality
  // paint on the header immediately without a manual reload.
  useEffect(() => {
    const onCustomerUpdated = (e) => {
      const updated = e?.detail?.customer_id;
      if (!updated || updated !== cid) return;
      try { load(); } catch { /* noop */ }
    };
    window.addEventListener("cocoon:customer-updated", onCustomerUpdated);
    return () => window.removeEventListener("cocoon:customer-updated", onCustomerUpdated);
  }, [cid, load]);
  // Stamp this customer as "viewed just now" so the Manage Customers list
  // stops showing a "new update" badge for their row until the next
  // customer-side change (portal medical form save, preference update, …).
  useEffect(() => { if (cid) markCustomerViewed(cid); }, [cid]);

  // Debounced customer search for the "Referred by" picker. Skipped when the
  // operator already selected someone (the chip is showing).
  useEffect(() => {
    if (referrer || !referrerQuery.trim()) { setReferrerResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await staffApi.get("/customers", { params: { search: referrerQuery.trim() } });
        setReferrerResults((r.data || []).filter((c) => c.id !== cid).slice(0, 8));
      } catch { setReferrerResults([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [referrerQuery, referrer, cid]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const save = async () => {
    setSaving(true);
    try {
      await staffApi.put(`/customers/${cid}`, form);
      toast.success("Customer updated");
      // Iter-261 — fan out a same-tab window event so every open
      // BranchCalendar / BranchOrders / MyRosterDay mounted in this
      // tab refetches immediately (before the WS round-trip). Combined
      // with the backend cascade broadcast, this guarantees the
      // calendar tile shows the new name without a manual reload.
      try {
        window.dispatchEvent(new CustomEvent("cocoon:customer-updated", {
          detail: { customer_id: cid },
        }));
      } catch { /* noop */ }
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  // A customer is "new" (worth flagging in the header) when they have at most
  // one completed booking so the front-desk can offer them onboarding perks.
  const completedCount = (data?.appointments || []).filter((a) => a.status === "completed").length;
  const isNew = data && completedCount <= 1;

  return (
    <StaffShell context="brand">
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(backTo.path)}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="cust-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {backTo.label}
        </button>
        <div className="flex items-start justify-between gap-4 mt-2 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold" data-testid="cust-detail-name">
              {loading ? "…" : `${form.first_name} ${form.last_name}`.trim() || (data?.customer.full_name || "Customer")}
            </h1>
            {isNew && (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200 rounded uppercase text-[10px] tracking-wider" data-testid="cust-new-badge">
                New
              </Badge>
            )}
            {/* Lifetime No-Show tally — one-glance signal for the front
                desk when they pull up a repeat offender. Hidden when
                the count is 0 so a clean record isn't visually noisy. */}
            {!loading && Number(data?.summary?.no_show_count || 0) > 0 && (
              <Badge
                className="bg-rose-100 text-rose-800 hover:bg-rose-100 border border-rose-200 rounded gap-1 text-[10px] tracking-wider uppercase"
                data-testid="cust-noshow-badge"
                title={data.summary.last_no_show_at ? `Last no-show: ${formatBali(data.summary.last_no_show_at)}` : "Lifetime no-show count"}
              >
                No-Show · {data.summary.no_show_count}
              </Badge>
            )}
            {/* One-tap Claude briefing — same modal the appointment context
                menu opens. Hidden while the initial customer fetch is in
                flight since we need the customer id anyway. */}
            {!loading && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBriefingOpen(true)}
                data-testid="cust-ai-briefing-btn"
                className="gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                AI Briefing
              </Button>
            )}
          </div>
          {/* Visit-span header block. Mirrors the Mindbody layout the operator
              shared: last visit on top, first visit below. Empty when the
              customer has no bookings yet. */}
          {data?.summary && (data.summary.last_appointment_at || data.summary.first_appointment_at) && (
            <div className="text-xs text-right leading-tight" data-testid="cust-visit-span">
              <div><span className="text-muted-foreground">Last Appointment Date: </span><span className="font-medium">{formatBali(data.summary.last_appointment_at) || "—"}</span></div>
              <div><span className="text-muted-foreground">First Appointment Date: </span><span className="font-medium">{formatBali(data.summary.first_appointment_at) || "—"}</span></div>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="summary" className="space-y-4">
        <TabsList className="bg-transparent p-0 border-b border-border rounded-none w-full justify-start gap-1 h-auto">
          <TabsTrigger
            value="summary"
            data-testid="cust-tab-summary"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5"
          >Summary</TabsTrigger>
          <TabsTrigger
            value="details"
            data-testid="cust-tab-profile"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5"
          >Profile</TabsTrigger>
          <TabsTrigger
            value="preferences"
            data-testid="cust-tab-preferences"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5"
          >Guest Preferences</TabsTrigger>
          <TabsTrigger
            value="medical-history"
            data-testid="cust-tab-medical-history"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5"
          >Medical History</TabsTrigger>
          <TabsTrigger
            value="activity-history"
            data-testid="cust-tab-activity-history"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5"
          >Activity History</TabsTrigger>
          <TabsTrigger
            value="goal"
            data-testid="cust-tab-goal"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5"
          >G.O.A.L</TabsTrigger>
          {longevityUnlocked && (
            <TabsTrigger
              value="longevity"
              data-testid="cust-tab-longevity"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5"
            >Longevity</TabsTrigger>
          )}
        </TabsList>

        {/* Summary — chronological visit history + recent orders. */}
        <TabsContent value="summary">
          {loading ? (
            <div className="text-sm text-muted-foreground p-6">Loading…</div>
          ) : (
            <div className="space-y-4">
              {/* Stat strip — loyalty balance, remaining series, YTD + lifetime
                  spend. Formatted with thousands separators (no decimals) to
                  match the rest of the CRM. */}
              {data.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cust-summary-stats">
                  <StatCard
                    icon={<Sparkles className="w-4 h-4" />}
                    label="Loyalty points"
                    value={Number(data.summary.loyalty_points || 0).toLocaleString("id-ID")}
                    accent="amber"
                    testid="stat-loyalty"
                    actions={
                      <>
                        <LoyaltyHistoryPopover cid={cid} />
                        <LoyaltyEditPopover cid={cid} onChanged={load} />
                      </>
                    }
                  />
                  <StatCard
                    icon={<Package className="w-4 h-4" />}
                    label="Series remaining"
                    value={(data.summary.series_active || []).reduce((n, s) => n + (Number(s.quantity_remaining) || 0), 0)}
                    hint={data.summary.series_active_count ? `${data.summary.series_active_count} active package${data.summary.series_active_count === 1 ? "" : "s"}` : "No active series"}
                    accent="sky"
                    testid="stat-series"
                  />
                  <StatCard
                    icon={<TrendingUp className="w-4 h-4" />}
                    label={`Total sales ${data.summary.current_year || ""}`}
                    value={`Rp ${Number(data.summary.total_sales_year || 0).toLocaleString("id-ID")}`}
                    accent="emerald"
                    testid="stat-sales-ytd"
                  />
                  <StatCard
                    icon={<Wallet className="w-4 h-4" />}
                    label="Lifetime sales"
                    value={`Rp ${Number(data.summary.total_sales_lifetime || 0).toLocaleString("id-ID")}`}
                    accent="rose"
                    testid="stat-sales-lifetime"
                  />
                </div>
              )}

              {/* Optional detail of the active series — collapsed by default
                  so the strip stays clean, but useful when the operator wants
                  to know WHICH package a session is being redeemed from. */}
              {data.summary?.series_active?.length > 0 && (
                <Card className="rounded-lg">
                  <CardContent className="p-0">
                    <div className="px-3 py-2 bg-sky-50 text-sky-900 text-xs font-medium border-b border-border">ACTIVE SERIES · {data.summary.series_active.length}</div>
                    <div className="divide-y divide-border">
                      {data.summary.series_active.map((s) => (
                        <div key={s.id} className="flex items-center justify-between px-3 py-2 text-xs gap-3" data-testid={`cust-series-row-${s.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">{s.series_name}</div>
                            <div
                              className={"text-[11px] " + (VALIDITY_TONE_CLASS[clientSeriesValidity(s).tone] || "text-muted-foreground")}
                              data-testid={`cust-series-validity-${s.id}`}
                            >
                              {clientSeriesValidity(s).text}
                            </div>
                          </div>
                          <div className="text-muted-foreground whitespace-nowrap"><span className="font-medium text-foreground">{s.quantity_remaining}</span>/{s.quantity_total} sessions left</div>
                          <div className="flex items-center gap-1">
                            <SeriesHistoryPopover cid={cid} sid={s.id} name={s.series_name} />
                            <SeriesEditPopover cid={cid} series={s} onChanged={load} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="rounded-lg">
                <CardContent className="p-0">
                  <div className="px-3 py-2 bg-sky-50 text-sky-900 text-xs font-medium border-b border-border flex items-center justify-between">
                    <span>RECENT APPOINTMENTS{data.appointments.length ? ` · showing ${Math.min(10, data.appointments.length)} of ${data.appointments.length}` : ""}</span>
                    {data.appointments.length > 10 && (
                      <button
                        type="button"
                        onClick={() => navigate(`/staff/brand/customers/${cid}/appointments`)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-800 hover:text-sky-950"
                        data-testid="cust-view-all-appts"
                      >
                        View all <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {data.appointments.length === 0 ? (
                    <p className="p-6 text-center text-xs text-muted-foreground" data-testid="cust-appts-empty">No appointment history yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="h-8">
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Booking #</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Status</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Service</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Appointment</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Location</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider text-right">Bill</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.appointments.slice(0, 10).map((a) => (
                            <TableRow key={a.id} className="h-9" data-testid={`cust-appt-row-${a.id}`}>
                              <TableCell className="py-1.5 text-xs text-muted-foreground font-mono">{a.id.slice(0, 8).toUpperCase()}</TableCell>
                              <TableCell className="py-1.5 text-xs">
                                <ApptStatusBadge status={a.status} refunded={!!a.payment_refunded} />
                              </TableCell>
                              <TableCell className="py-1.5 text-xs">{a.service_name}</TableCell>
                              <TableCell className="py-1.5 text-xs whitespace-nowrap">{formatBali(a.start_iso)}</TableCell>
                              <TableCell className="py-1.5 text-xs text-muted-foreground">{branchName(a.branch_id)}</TableCell>
                              <TableCell className="py-1.5 text-right">
                                {/*
                                 * Eye is available for EVERY appointment
                                 * regardless of status. If an order
                                 * already covers the visit we open it;
                                 * otherwise `?appt=<id>` triggers the
                                 * existing seed-draft-order flow so the
                                 * front-desk can Take Deposit / Add
                                 * Items right from the profile. The
                                 * checkout page itself still gates
                                 * "Take Payment" on Check-In, so we're
                                 * not bypassing that rule.
                                 */}
                                <button
                                  type="button"
                                  onClick={() => navigate(
                                    a.order_id
                                      ? `/staff/branch/${a.branch_id}/orders?order=${a.order_id}`
                                      : `/staff/branch/${a.branch_id}/orders?appt=${a.id}`
                                  )}
                                  className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-primary/10 text-primary"
                                  title={a.order_id ? "Open order / billing" : "Open checkout for this booking"}
                                  data-testid={`cust-appt-bill-${a.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="rounded-lg">
                <CardContent className="p-0">
                  <div className="px-3 py-2 bg-sky-50 text-sky-900 text-xs font-medium border-b border-border flex items-center justify-between">
                    <span>RECENT ORDERS{data.orders.length ? ` · showing ${Math.min(10, data.orders.length)} of ${data.orders.length}` : ""}</span>
                    {data.orders.length > 10 && (
                      <button
                        type="button"
                        onClick={() => navigate(`/staff/brand/customers/${cid}/orders`)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-800 hover:text-sky-950"
                        data-testid="cust-view-all-orders"
                      >
                        View all <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {data.orders.length === 0 ? (
                    <p className="p-6 text-center text-xs text-muted-foreground" data-testid="cust-orders-empty">There are no completed orders.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="h-8">
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Order #</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Status</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Total</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Location</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Created</TableHead>
                            <TableHead className="py-1.5 text-[11px] uppercase tracking-wider text-right">Open</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.orders.slice(0, 10).map((o) => (
                            <TableRow key={o.id} className="h-9" data-testid={`cust-order-row-${o.id}`}>
                              <TableCell className="py-1.5 text-xs text-muted-foreground font-mono">{o.order_number || o.id.slice(0, 8).toUpperCase()}</TableCell>
                              <TableCell className="py-1.5 text-xs">{o.status}</TableCell>
                              <TableCell className="py-1.5 text-xs whitespace-nowrap">Rp {Number(o.total || 0).toLocaleString("id-ID")}</TableCell>
                              <TableCell className="py-1.5 text-xs text-muted-foreground">{branchName(o.branch_id)}</TableCell>
                              <TableCell className="py-1.5 text-xs text-muted-foreground">{formatBali(o.created_at)}</TableCell>
                              <TableCell className="py-1.5 text-right">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/staff/branch/${o.branch_id}/orders?order=${o.id}`)}
                                  className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-primary/10 text-primary"
                                  title="Open order / billing"
                                  data-testid={`cust-order-open-${o.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Details — editable profile card. */}
        <TabsContent value="details">
          <Card className="rounded-lg max-w-3xl">
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cust-first">First name</Label>
                  <Input id="cust-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="cust-first-input" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cust-last">Last name</Label>
                  <Input id="cust-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="cust-last-input" className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cust-email">Email</Label>
                  <Input id="cust-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email-input" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="cust-wa">WhatsApp / phone</Label>
                  <div className="mt-1">
                    <PhoneInput
                      value={form.whatsapp}
                      onChange={(v) => setForm({ ...form, whatsapp: v })}
                      testId="cust-whatsapp-input"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="cust-nat">Nationality</Label>
                  <div className="mt-1">
                    <NationalityCombobox
                      value={form.nationality}
                      onChange={(v) => setForm({ ...form, nationality: v })}
                      testId="cust-nationality-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="cust-dob">Birthday</Label>
                  <Input id="cust-dob" type="date" value={form.birthday || ""} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="cust-birthday-input" className="mt-1" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={form.gender || "unspecified"} onValueChange={(v) => setForm({ ...form, gender: v === "unspecified" ? "" : v })}>
                    <SelectTrigger data-testid="cust-gender-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unspecified">Prefer not to say</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Referred by — searchable customer picker that awards the
                  referrer 200 loyalty points once, server-side. Staff can
                  still edit the referrer after the fact, but the +200 bonus
                  is only issued the FIRST time the field is set (guarded by
                  the atomic `referral_awarded_at` CAS in the backend). */}
              <div className="pt-4 border-t border-border">
                <Label className="text-sm">Referred by</Label>
                <p className="text-[11px] text-muted-foreground mb-2">
                  {data?.customer?.referral_awarded_at
                    ? `Referral loyalty (+200 pts) was already issued on ${new Date(data.customer.referral_awarded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}. You can still update this field, but the bonus will not be paid a second time.`
                    : "The person named here is credited 200 loyalty points automatically the first time this field is saved."}
                </p>
                {referrer ? (
                  <div className="flex items-center gap-2" data-testid="referrer-chip">
                    <div className="rounded-md border border-border bg-muted/30 px-3 py-1.5 text-sm">
                      <span className="font-medium">{referrer.full_name || `${referrer.first_name || ""} ${referrer.last_name || ""}`.trim() || referrer.email}</span>
                      <span className="text-xs text-muted-foreground ml-2">{referrer.email || referrer.whatsapp || ""}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setReferrer(null); setForm({ ...form, referred_by_customer_id: "" }); }} data-testid="referrer-clear">Change</Button>
                  </div>
                ) : (
                  <>
                    <Input
                      placeholder="Search by name, email or phone…"
                      value={referrerQuery}
                      onChange={(e) => setReferrerQuery(e.target.value)}
                      data-testid="referrer-search-input"
                      className="max-w-md"
                    />
                    {referrerResults.length > 0 && (
                      <div className="mt-1 border border-border rounded-md max-w-md max-h-56 overflow-auto bg-white shadow-sm">
                        {referrerResults.map((c) => (
                          <button
                            type="button"
                            key={c.id}
                            className="w-full text-left px-3 py-2 hover:bg-muted/50 text-sm"
                            onClick={() => {
                              setReferrer(c);
                              setForm({ ...form, referred_by_customer_id: c.id });
                              setReferrerQuery("");
                              setReferrerResults([]);
                            }}
                            data-testid={`referrer-pick-${c.id}`}
                          >
                            <div className="font-medium">{c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}</div>
                            <div className="text-[11px] text-muted-foreground">{c.email || c.whatsapp || ""}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Pre-Treatment Medical Questionnaire — collapsed by default
                  because the 19-row grid takes a lot of vertical space. A
                  small counter on the header hints at how many answers are
                  already on file so operators know it's worth expanding. */}
              <div className="pt-4 border-t border-border">
                <Collapsible>
                  <CollapsibleTrigger
                    className="w-full flex items-center justify-between gap-2 group py-1 -mx-1 px-1 rounded hover:bg-muted/40"
                    data-testid="cust-medical-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <Label className="text-sm cursor-pointer">Medical profile</Label>
                      {(() => {
                        // Show a small count of already-answered questions so
                        // operators see whether there's anything on file
                        // without expanding the whole section.
                        const answered = Object.values(form.medical_questionnaire || {})
                          .filter((v) => v && (v.answer === "yes" || v.answer === "no")).length;
                        const yesCount = Object.values(form.medical_questionnaire || {})
                          .filter((v) => v && v.answer === "yes").length;
                        const hasAllergies = !!(form.medication_allergies || "").trim() ||
                          !!(form.current_medications || "").trim() ||
                          (Array.isArray(form.cosmetic_allergies) && form.cosmetic_allergies.length > 0);
                        if (!answered && !hasAllergies) return null;
                        return (
                          <span className="text-[11px] text-muted-foreground" data-testid="cust-medical-summary">
                            {answered ? `${answered}/${MEDICAL_QUESTIONS.length} answered` : ""}
                            {answered && yesCount ? " · " : ""}
                            {yesCount ? (
                              <span className="text-amber-700">{yesCount} yes</span>
                            ) : null}
                            {hasAllergies && (answered || yesCount) ? " · " : ""}
                            {hasAllergies ? "allergy notes" : ""}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-3">
                      {data?.customer?.medical_updated_at && (
                        <span className="text-[11px] text-muted-foreground hidden sm:inline" data-testid="cust-medical-last-update">
                          Last updated {formatBali(data.customer.medical_updated_at) || "—"}
                          {data.customer.medical_updated_by ? ` by ${data.customer.medical_updated_by}` : ""}
                        </span>
                      )}
                      <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                    </div>
                  </CollapsibleTrigger>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Pre-treatment questionnaire. Fill in what the guest has shared; you can add more on their next visit.
                  </p>
                  <CollapsibleContent className="mt-3" data-testid="cust-medical-panel">

                <div className="space-y-3">
                  <div>
                    <Label htmlFor="med-allergies" className="text-xs text-muted-foreground uppercase tracking-wider">Medication Allergies</Label>
                    <Textarea
                      id="med-allergies"
                      value={form.medication_allergies || ""}
                      onChange={(e) => setForm({ ...form, medication_allergies: e.target.value })}
                      rows={2}
                      placeholder="e.g. Penicillin, Sulfa drugs, Latex…"
                      className="mt-1"
                      data-testid="cust-medication-allergies"
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Cosmetic Allergy Ingredients</Label>
                    <p className="text-[11px] text-muted-foreground mb-2">Tick every ingredient this guest reacts to.</p>
                    {/* 3-column grid so all 11 boxes fit above the fold on
                        the profile tab without excessive vertical scroll. */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2" data-testid="cust-cosmetic-allergies">
                      {COSMETIC_INGREDIENTS.map((ing) => {
                        const checked = (form.cosmetic_allergies || []).includes(ing);
                        return (
                          <label
                            key={ing}
                            className="flex items-center gap-2 text-sm cursor-pointer select-none"
                            data-testid={`cust-cosmetic-${ing.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                const cur = new Set(form.cosmetic_allergies || []);
                                if (v) cur.add(ing); else cur.delete(ing);
                                setForm({ ...form, cosmetic_allergies: Array.from(cur) });
                              }}
                            />
                            <span>{ing}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="cur-meds" className="text-xs text-muted-foreground uppercase tracking-wider">
                      Current Prescription &amp; Over-the-Counter Medications
                    </Label>
                    <p className="text-[11px] text-muted-foreground mb-1">Include Aspirin, Ibuprofen, herbs, vitamins.</p>
                    <Textarea
                      id="cur-meds"
                      value={form.current_medications || ""}
                      onChange={(e) => setForm({ ...form, current_medications: e.target.value })}
                      rows={2}
                      placeholder="e.g. Aspirin 100mg daily, multivitamin, fish oil…"
                      className="mt-1"
                      data-testid="cust-current-medications"
                    />
                  </div>

                  {/* Pre-Treatment Medical Questionnaire grid — Question / Yes /
                      No / Comments. Uses the shared MEDICAL_QUESTIONS list so
                      order + labels stay consistent everywhere the guide is
                      shown (e.g. future PDF export / staff printout). */}
                  <div className="pt-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">Pre-Treatment Medical Questionnaire</Label>
                    <div className="mt-2 rounded-md border border-border overflow-hidden">
                      <div className="grid grid-cols-[1fr_60px_60px_1.4fr] gap-2 px-3 py-2 bg-secondary/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <span>Do you have</span><span className="text-center">Yes</span><span className="text-center">No</span><span>Comments</span>
                      </div>
                      {MEDICAL_QUESTIONS.map((q, idx) => {
                        const val = form.medical_questionnaire?.[q.key] || {};
                        const setAnswer = (ans) => {
                          const next = { ...(form.medical_questionnaire || {}) };
                          next[q.key] = { ...(next[q.key] || {}), answer: ans };
                          setForm({ ...form, medical_questionnaire: next });
                        };
                        const setNote = (note) => {
                          const next = { ...(form.medical_questionnaire || {}) };
                          next[q.key] = { ...(next[q.key] || {}), note };
                          setForm({ ...form, medical_questionnaire: next });
                        };
                        return (
                          <div
                            key={q.key}
                            className={
                              "grid grid-cols-[1fr_60px_60px_1.4fr] gap-2 px-3 py-2 text-sm items-center " +
                              (idx > 0 ? "border-t border-border/50 " : "")
                            }
                            data-testid={`cust-mq-row-${q.key}`}
                          >
                            <span className="pr-2">{q.label}</span>
                            <RadioGroup
                              value={val.answer || ""}
                              onValueChange={setAnswer}
                              className="contents"
                            >
                              <div className="flex justify-center">
                                <RadioGroupItem value="yes" id={`mq-${q.key}-yes`} data-testid={`cust-mq-${q.key}-yes`} />
                              </div>
                              <div className="flex justify-center">
                                <RadioGroupItem value="no" id={`mq-${q.key}-no`} data-testid={`cust-mq-${q.key}-no`} />
                              </div>
                            </RadioGroup>
                            <Input
                              value={val.note || ""}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="Comments…"
                              className="h-8"
                              data-testid={`cust-mq-note-${q.key}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              {/* Email types — moved to sit BELOW the Staff-only alerts per
                  operator request. Toggles are honoured by the delivery
                  engine (transactional gates lifecycle emails, promotional
                  gates birthdays / after-paid follow-ups). */}
              <div className="pt-4 border-t border-border">
                <Label className="text-sm">Email types</Label>
                <p className="text-[11px] text-muted-foreground mb-2">Untick to unsubscribe. Templates whose trigger belongs to that family will skip this customer.</p>
                <div className="flex items-center gap-6" data-testid="email-types-row">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-rose-600"
                      checked={!!form.email_transactional_opt_in}
                      onChange={(e) => setForm({ ...form, email_transactional_opt_in: e.target.checked })}
                      data-testid="opt-transactional"
                    /> Transactional (bookings, payments, cancels)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-rose-600"
                      checked={!!form.email_promotional_opt_in}
                      onChange={(e) => setForm({ ...form, email_promotional_opt_in: e.target.checked })}
                      data-testid="opt-promotional"
                    /> Promotional (birthday, follow-ups)
                  </label>
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => load()} disabled={saving} data-testid="cust-cancel-btn">Cancel</Button>
                <Button onClick={save} disabled={saving} className="gap-1" data-testid="cust-save-btn">
                  <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guest Preferences — staff-only notes with per-entry Booking /
            Payment alert flags. Popups fire in the New Appointment modal and
            on the Checkout page. Never shown to the customer. */}
        <TabsContent value="preferences">
          <GuestPreferencesTab
            prefs={form.guest_preferences}
            onChange={async (next) => {
              // Persist inline so operators don't need to click Save on the
              // Profile tab for their preference changes to take effect.
              setForm((f) => ({ ...f, guest_preferences: next, booking_alert: "", payment_alert: "" }));
              try {
                await staffApi.put(`/customers/${cid}`, {
                  guest_preferences: next,
                  // Zero out the legacy fields once we've moved their
                  // contents into the structured list — prevents duplicate
                  // popups from firing on old + new data.
                  booking_alert: "",
                  payment_alert: "",
                });
                toast.success("Preferences updated");
              } catch (err) { toast.error(formatApiError(err)); }
            }}
          />
        </TabsContent>

        <TabsContent value="medical-history">
          <MedicalHistoryTab cid={cid} />
        </TabsContent>

        {/* Iter-366 — Longevity Dashboard. Biometric Journey Vault
            (body composition, blood markers, VO2 Max, wellness scores)
            gated to staff with customer.edit rights via the same
            backend guardrails as the rest of this profile.
            Iter-367 — additionally hidden behind the staff-email
            whitelist so only the Longevity access ring sees the tab. */}
        {longevityUnlocked && (
          <TabsContent value="longevity">
            <LongevityTab cid={cid} customer={data?.customer} />
          </TabsContent>
        )}

        {/* Activity History (iter-190). Renders `customer.audit`
            newest-first — profile create, edits, merges. Iconography
            is a simple bullet so operators skim quickly. Hidden from
            the customer portal because /auth/me projects `audit: 0`. */}
        <TabsContent value="activity-history">
          <ActivityHistoryTab audit={data?.customer?.audit || []} />
        </TabsContent>

        {/* G.O.A.L — this guest's slice of the brand-wide Guest Opportunity
            Action Learning log. Same records as Brand Catalog → Customers →
            G.O.A.L; writes from either view appear in both. */}
        <TabsContent value="goal">
          <GoalTab
            active
            customerId={cid}
            customerName={`${form.first_name} ${form.last_name}`.trim() || data?.customer?.full_name || ""}
            customer={data?.customer}
          />
        </TabsContent>
      </Tabs>

      <AIBriefingModal
        open={briefingOpen}
        onOpenChange={setBriefingOpen}
        customerId={cid}
        customerName={`${form.first_name} ${form.last_name}`.trim() || data?.customer?.full_name || "Guest"}
      />
    </StaffShell>
  );
}

// Activity History (iter-190). Renders the customer's `audit` array
// newest-first: profile creation, per-field edits, merges. The array
// is fetched with the customer doc via GET /customers/{cid}. Empty
// state kept short — most guests won't have edits yet.
function ActivityHistoryTab({ audit }) {
  const rows = React.useMemo(() => {
    const sorted = Array.isArray(audit) ? audit.slice() : [];
    sorted.sort((a, b) => String(b?.at || "").localeCompare(String(a?.at || "")));
    return sorted;
  }, [audit]);
  return (
    <Card className="rounded-lg" data-testid="cust-activity-history">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Activity history</span>
          <Badge variant="outline" className="rounded-full text-[10px]" data-testid="cust-activity-count">
            {rows.length} event{rows.length === 1 ? "" : "s"}
          </Badge>
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="cust-activity-empty">
            No profile activity yet.
          </div>
        ) : (
          <ol className="relative space-y-3 pl-4 border-l border-border">
            {rows.map((e) => (
              <li key={e.id || `${e.at}-${e.kind}`} className="relative" data-testid={`cust-activity-event-${e.id || e.kind}`}>
                <span className="absolute -left-[7px] top-1.5 w-2.5 h-2.5 rounded-full bg-primary/70" aria-hidden />
                <div className="text-[11px] text-muted-foreground">
                  {formatLongDate(e.at)} · {e.by_name || "staff"}
                </div>
                <div className="text-sm text-slate-800">{e.description}</div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}



/* ---------------- Medical History Tab (clinical-only timeline) ---------------- */
function MedicalHistoryTab({ cid }) {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  // Iter-334 · Edit path reuses `MedicalRecordDialog` — the SAME form
  // used for initial input. No separate edit component, no duplicate
  // state. Clicking the Pencil icon opens the dialog populated with
  // the existing record; the dialog's own useEffect fetches
  // `GET /appointments/{aid}/medical-record` and Save posts to
  // `PUT /appointments/{aid}/medical-record`. The backend already
  // bypasses the "TODAY check-in" Medical History Lockdown gate for
  // EDIT paths (existing row), so `brand.customers.edit` role
  // holders can correct historical records without a time limit.
  const [editAppt, setEditAppt] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);

  const reload = React.useCallback(async () => {
    try {
      const r = await staffApi.get(`/staff/customer-medical-profile/${cid}`);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [cid]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await staffApi.get(`/staff/customer-medical-profile/${cid}`);
        if (mounted) setData(r.data);
      } catch (err) {
        if (mounted) toast.error(formatApiError(err));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [cid]);

  const canEdit = !!data?.permissions?.can_edit;
  const canDelete = !!data?.permissions?.can_delete;
  const customerName = data?.customer?.full_name || data?.full_name || "";

  const openEdit = (h) => setEditAppt({
    id: h.appointment_id,
    customer_name: customerName,
    service_name: h.service_name,
    start_iso: h.start_iso || h.updated_at || h.created_at,
    status: h.status || "",
    // The dialog's `hasExistingRecord` short-circuits the lockdown
    // banner once the GET returns a populated record, so this
    // sentinel just keeps the checked-in-today flag consistent
    // while the load is in flight. For an EMPTY session the real
    // `checked_in_at` matters: the backend only accepts a brand-new
    // record once the guest has actually attended, so passing the
    // true value makes the dialog's banner match the server's answer.
    checked_in_at: h.checked_in_at || (h.has_record ? (h.updated_at || h.created_at) : ""),
  });

  const confirmDelete = async (aid) => {
    if (!window.confirm("Delete this medical record? Linked-session siblings will also be cleared. This cannot be undone.")) return;
    setDeletingId(aid);
    try {
      await staffApi.delete(`/appointments/${aid}/medical-record`);
      toast.success("Medical record deleted");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground p-6">Loading…</div>;
  if (!data) return <div className="text-sm text-muted-foreground p-6">No records.</div>;

  const days = data.history_by_date || [];
  return (
    <div className="rounded-xl bg-white border border-border p-4" data-testid="cust-medical-history">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold">Full Medical History</h2>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {days.length} visit day{days.length === 1 ? "" : "s"}
        </span>
      </div>
      {days.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No medical records saved yet across the brand.</p>
      ) : (
        <Accordion type="multiple" className="space-y-2" defaultValue={[days[0]?.date].filter(Boolean)}>
          {days.map((day) => {
            const groups = new Map();
            for (const e of day.entries) {
              const key = e.package_id || "__solo__";
              if (!groups.has(key)) groups.set(key, { key, package_name: e.package_name || "", entries: [] });
              groups.get(key).entries.push(e);
            }
            const groupList = [...groups.values()];
            const totalServices = day.entries.length;
            const headerLabel = groupList.length === 1 && groupList[0].package_name
              ? groupList[0].package_name
              : groupList.length === 1
                ? groupList[0].entries[0]?.service_name || "Session"
                : `${totalServices} services`;
            return (
              <AccordionItem key={day.date} value={day.date} className="rounded-lg border border-border overflow-hidden" data-testid={`cust-medical-day-${day.date}`}>
                <AccordionTrigger className="px-3 py-2.5 hover:no-underline">
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-semibold tabular-nums">{formatLongDate(day.date)}</div>
                    {day.entries[0]?.branch_name && (
                      <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`cust-medical-day-branch-${day.date}`}>
                        {[...new Set(day.entries.map((e) => e.branch_name).filter(Boolean))].join(" · ")}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground truncate">
                      {headerLabel}
                      <span className="ml-1 text-[11px]">· {totalServices} service{totalServices === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="space-y-3">
                    {groupList.map((grp) => (
                      <div key={grp.key} className="rounded-lg border border-border/70 bg-secondary/30 p-3">
                        {grp.package_name && (
                          <div className="flex items-center gap-1 text-[12px] font-medium text-primary mb-2">
                            <span className="truncate">{grp.package_name}</span>
                          </div>
                        )}
                        <ul className="space-y-2">
                          {grp.entries.map((h) => {
                            const latestEdit = Array.isArray(h.edit_history) && h.edit_history.length
                              ? h.edit_history[h.edit_history.length - 1]
                              : null;
                            const timeLabel = formatBaliTime(h.updated_at || h.created_at || h.start_iso);
                            return (
                              <li
                                key={h.appointment_id}
                                className="text-xs group"
                                data-testid={`cust-history-entry-${h.appointment_id}`}
                              >
                                <div className="flex items-start gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                                      <span>{h.service_name}</span>
                                      {timeLabel && (
                                        <span
                                          className="text-[10px] font-medium tabular-nums text-primary bg-primary/10 border border-primary/15 rounded-full px-1.5 py-0.5"
                                          data-testid={`cust-history-time-${h.appointment_id}`}
                                          title={h.updated_at || h.created_at}
                                        >
                                          {timeLabel}
                                        </span>
                                      )}
                                      {h.team && h.team.length > 0 && (
                                        <span className="text-xs text-muted-foreground font-normal">
                                          — {h.team.map((t) => t.name).join(", ")}
                                        </span>
                                      )}
                                      {h.visit_cancelled && (
                                        <span
                                          className="text-[10px] font-semibold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5"
                                          data-testid={`cust-history-cancelled-${h.appointment_id}`}
                                          title="This visit was cancelled — the note below was recorded before it was cancelled."
                                        >
                                          Cancelled visit
                                        </span>
                                      )}
                                    </div>
                                    {latestEdit && (
                                      <div
                                        className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                                        data-testid={`cust-history-edited-${h.appointment_id}`}
                                        title={`Previous notes: ${latestEdit.prev_session_notes || "(empty)"}`}
                                      >
                                        <History className="w-3 h-3" />
                                        Edited on {formatBaliDate(latestEdit.edited_at)} by {latestEdit.edited_by_name || "staff"}
                                      </div>
                                    )}
                                  </div>
                                  {h.has_record ? (
                                    (canEdit || canDelete) && (
                                      <div className="flex items-center gap-1 shrink-0">
                                        {canEdit && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2"
                                            onClick={() => openEdit(h)}
                                            data-testid={`cust-history-edit-${h.appointment_id}`}
                                            title="Edit this record"
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </Button>
                                        )}
                                        {canDelete && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                            onClick={() => confirmDelete(h.appointment_id)}
                                            disabled={deletingId === h.appointment_id}
                                            data-testid={`cust-history-delete-${h.appointment_id}`}
                                            title="Delete this record"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        )}
                                      </div>
                                    )
                                  ) : (
                                    /* Jun-2026 · an EMPTY session had no
                                       affordance at all, so a missed note
                                       could not be entered from the guest
                                       profile even by the Owner. The server
                                       accepts a retroactive write for any
                                       ATTENDED visit (no time window), so
                                       anyone with Customers · Edit gets an
                                       Add-note button here. */
                                    canEdit && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 shrink-0 text-[11px] gap-1"
                                        onClick={() => openEdit(h)}
                                        data-testid={`cust-history-add-${h.appointment_id}`}
                                        title="Add the medical note for this session"
                                      >
                                        <Plus className="w-3.5 h-3.5" /> Add note
                                      </Button>
                                    )
                                  )}
                                </div>
                                <div className="mt-1 whitespace-pre-wrap space-y-1">
                                  {(h.session_notes || h.notes) && <p><b>Notes:</b> {h.session_notes || h.notes}</p>}
                                  {h.internal_notes && (
                                    <p className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-amber-900">
                                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold mr-1">
                                        <Lock className="w-3 h-3" /> Internal
                                      </span>
                                      {h.internal_notes}
                                    </p>
                                  )}
                                  {h.treatments_done && <p><b>Treatments:</b> {h.treatments_done}</p>}
                                  {h.follow_up && <p><b>Follow-up:</b> {h.follow_up}</p>}
                                  {!h.has_record && (
                                    <p className="italic text-muted-foreground">No medical note yet for this session.</p>
                                  )}
                                  <AttachmentThumbnails
                                    attachments={h.attachments}
                                    testidPrefix={`cust-history-attachments-${h.appointment_id}`}
                                  />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Iter-334 · Edit reuses the SAME MedicalRecordDialog used
          for initial input. Populated from the appointment id — the
          dialog fetches the existing row on mount and posts to
          `PUT /appointments/{aid}/medical-record` on save. Closing
          triggers a reload so the "Edited on …" chip repaints. */}
      {editAppt && (
        <MedicalRecordDialog
          open={!!editAppt}
          onOpenChange={(o) => {
            if (!o) {
              setEditAppt(null);
              reload();
            }
          }}
          appt={editAppt}
        />
      )}
    </div>
  );
}

/** HH:MM in Bali time — used on every history card to attribute the
 *  precise moment the record was last updated. */
function formatBaliTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", {
      timeZone: "Asia/Makassar",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return ""; }
}

/** Short Bali date (e.g. "12 Feb 2026") for the "Edited on …" flag. */
function formatBaliDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      timeZone: "Asia/Makassar",
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return ""; }
}

/** Format an ISO date "2026-08-20" as "August 20, 2026" for the
 *  accordion header. Falls back to the raw string on parse errors. */
function formatLongDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}


function formatBali(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar",
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
}

function ApptStatusBadge({ status, refunded = false }) {
  const s = status || "booked";
  const map = {
    booked: "bg-emerald-100 text-emerald-700 border-emerald-200",
    in_progress: "bg-sky-100 text-sky-700 border-sky-200",
    completed: "bg-muted text-muted-foreground border-border",
    cancelled: "bg-rose-100 text-rose-700 border-rose-200",
    no_show: "bg-amber-100 text-amber-800 border-amber-200",
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-flex items-center rounded-full border h-5 px-2 text-[10px] uppercase tracking-wider ${map[s] || map.booked}`}>
        {s.replace("_", " ")}
      </span>
      {refunded && (
        <span className="inline-flex items-center rounded-full border h-5 px-2 text-[10px] uppercase tracking-wider bg-rose-100 text-rose-700 border-rose-200">
          refunded
        </span>
      )}
    </span>
  );
}


// Compact stat tile used on the Summary tab. `accent` maps to a Tailwind
// palette so each tile carries a subtle brand tone without needing bespoke
// classes.
function StatCard({ icon, label, value, hint, accent = "sky", testid, actions }) {
  const palette = {
    amber:   { bg: "bg-amber-50",   ring: "border-amber-200",   ink: "text-amber-900",   dot: "text-amber-600" },
    sky:     { bg: "bg-sky-50",     ring: "border-sky-200",     ink: "text-sky-900",     dot: "text-sky-600" },
    emerald: { bg: "bg-emerald-50", ring: "border-emerald-200", ink: "text-emerald-900", dot: "text-emerald-600" },
    rose:    { bg: "bg-rose-50",    ring: "border-rose-200",    ink: "text-rose-900",    dot: "text-rose-600" },
  }[accent] || {};
  return (
    <div className={`rounded-lg border ${palette.ring} ${palette.bg} px-3 py-2.5 relative`} data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider ${palette.ink}`}>
        <span className={palette.dot}>{icon}</span>{label}
      </div>
      <div className={`text-xl font-semibold mt-1 ${palette.ink}`} data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
      {hint && <div className={`text-[11px] mt-0.5 ${palette.ink} opacity-70`}>{hint}</div>}
      {actions && (
        <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5">
          {actions}
        </div>
      )}
    </div>
  );
}


// ---------------- Loyalty popovers -----------------------------------------
// Small icon-triggered popover that lists the full loyalty-point ledger. Data
// is fetched lazily on open so we don't stress the summary endpoint.
function LoyaltyHistoryPopover({ cid }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!open) return;
    setRows(null);
    setSummary(null);
    staffApi.get(`/customers/${cid}/loyalty/history`)
      .then((r) => {
        setRows(r.data.rows || []);
        setSummary(r.data.summary || null);
      })
      .catch(() => setRows([]));
  }, [open, cid]);
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="View history"
          className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-amber-100/60 text-amber-700"
          data-testid="loyalty-history-btn"
        >
          <Clock className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 max-h-96 overflow-y-auto" data-testid="loyalty-history-popover">
        <div className="px-3 py-2 border-b border-border bg-amber-50 text-amber-900 text-[11px] uppercase tracking-wider">Loyalty history</div>
        {/* Rolling-expiry summary strip. Only shown when there ARE points
            expiring within 30 days so the popover stays quiet otherwise. */}
        {summary && Number(summary.points_expiring_soon) > 0 && (
          <div className="px-3 py-2 border-b border-amber-200 bg-amber-50/60 text-[11px] text-amber-900" data-testid="loyalty-expiring-strip">
            <div className="flex items-center gap-1 font-semibold">
              <AlertTriangle className="w-3.5 h-3.5" />
              {Number(summary.points_expiring_soon).toLocaleString("id-ID")} pts expire within 30 days
            </div>
            {summary.next_expiry_at && (
              <div className="text-[10px] text-amber-800/80 mt-0.5">Next: {Number(summary.next_expiry_points).toLocaleString("id-ID")} pts on {fmtDate(summary.next_expiry_at)}</div>
            )}
          </div>
        )}
        {rows === null && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
        {rows && rows.length === 0 && <div className="p-4 text-xs text-muted-foreground" data-testid="loyalty-history-empty">No ledger entries yet.</div>}
        {rows && rows.length > 0 && (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <li key={r.id} className="px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className={`font-semibold ${r.delta > 0 ? "text-emerald-700" : (r.delta < 0 ? "text-rose-700" : "text-amber-700")}`}>{r.delta > 0 ? "+" : ""}{r.delta}</span>
                  <span className="text-[10px] text-muted-foreground">{formatBali(r.at)}</span>
                </div>
                <div className="text-muted-foreground">{r.reason || r.kind || "manual"}</div>
                {r.kind === "earn" && r.expires_at && (
                  <div className="text-[10px] text-amber-700/80">Expires {fmtDate(r.expires_at)}</div>
                )}
                {r.kind === "expiry_warning" && (
                  <div className="text-[10px] text-amber-700/80">Warning email sent</div>
                )}
                {r.kind === "expired" && (
                  <div className="text-[10px] text-rose-700/80">Points expired</div>
                )}
                {(r.by_staff_name || r.by_name) && <div className="text-[10px] text-muted-foreground/80">by {r.by_staff_name || r.by_name}</div>}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Pencil-triggered "add / deduct" form. Uses a positive or negative delta so
// the ledger reads correctly.
function LoyaltyEditPopover({ cid, onChanged }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState("add");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAmount(""); setDirection("add"); setReason(""); } }, [open]);
  const submit = async () => {
    const n = parseInt(amount, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a positive number"); return; }
    const delta = direction === "deduct" ? -n : n;
    setBusy(true);
    try {
      await staffApi.post(`/customers/${cid}/loyalty/adjust`, { delta, reason });
      toast.success(`${direction === "deduct" ? "-" : "+"}${n} points`);
      setOpen(false);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Adjust balance" className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-amber-100/60 text-amber-700" data-testid="loyalty-edit-btn">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" data-testid="loyalty-edit-popover">
        <div className="text-[11px] uppercase tracking-wider text-amber-900 font-semibold mb-2">Adjust loyalty points</div>
        <div className="flex items-center gap-1 mb-2">
          <button type="button" onClick={() => setDirection("add")} className={`flex-1 h-7 text-xs rounded border ${direction === "add" ? "bg-emerald-100 border-emerald-300 text-emerald-900" : "border-border text-muted-foreground"}`} data-testid="loyalty-dir-add">+ Add</button>
          <button type="button" onClick={() => setDirection("deduct")} className={`flex-1 h-7 text-xs rounded border ${direction === "deduct" ? "bg-rose-100 border-rose-300 text-rose-900" : "border-border text-muted-foreground"}`} data-testid="loyalty-dir-deduct">− Deduct</button>
        </div>
        <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="h-8 text-sm mb-2" data-testid="loyalty-amount-input" autoFocus />
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="h-8 text-sm mb-3" data-testid="loyalty-reason-input" />
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="loyalty-save-btn">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------- Series popovers ------------------------------------------
function SeriesHistoryPopover({ cid, sid, name }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open) return;
    setRows(null);
    staffApi.get(`/customers/${cid}/series/${sid}/history`)
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]));
  }, [open, cid, sid]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title={`History · ${name}`} className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-sky-100/60 text-sky-700" data-testid={`series-history-btn-${sid}`}>
          <Clock className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 max-h-96 overflow-y-auto" data-testid={`series-history-popover-${sid}`}>
        <div className="px-3 py-2 border-b border-border bg-sky-50 text-sky-900 text-[11px] uppercase tracking-wider truncate">History · {name}</div>
        {rows === null && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
        {rows && rows.length === 0 && <div className="p-4 text-xs text-muted-foreground">No events yet.</div>}
        {rows && rows.length > 0 && (
          <ul className="divide-y divide-border">
            {rows.map((r, i) => (
              <li key={i} className="px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold capitalize">{r.kind}</span>
                  <span className="text-[10px] text-muted-foreground">{formatBali(r.at)}</span>
                </div>
                <div className="text-muted-foreground">{r.detail}</div>
                {r.note && <div className="text-[10px] italic text-muted-foreground/80">{r.note}</div>}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Pencil-triggered quick-edit for a single series. Lets the operator bump the
// remaining sessions and/or extend the expiry date.
function SeriesEditPopover({ cid, series, onChanged }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(String(series.quantity_remaining ?? ""));
  const [expiry, setExpiry] = useState((series.expires_at || "").slice(0, 10));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setQty(String(series.quantity_remaining ?? ""));
      setExpiry((series.expires_at || "").slice(0, 10));
      setReason("");
    }
  }, [open, series]);
  const submit = async () => {
    const payload = {};
    const parsed = parseInt(qty, 10);
    if (Number.isFinite(parsed) && parsed !== (series.quantity_remaining ?? -1)) payload.quantity_remaining = parsed;
    const nextExpiry = expiry ? `${expiry}T23:59:59+00:00` : null;
    if ((nextExpiry || null) !== (series.expires_at || null)) payload.expires_at = nextExpiry;
    payload.reason = reason;
    if (payload.quantity_remaining === undefined && payload.expires_at === undefined) { setOpen(false); return; }
    setBusy(true);
    try {
      await staffApi.put(`/customers/${cid}/series/${series.id}`, payload);
      toast.success("Series updated");
      setOpen(false);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Edit series" className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-sky-100/60 text-sky-700" data-testid={`series-edit-btn-${series.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" data-testid={`series-edit-popover-${series.id}`}>
        <div className="text-[11px] uppercase tracking-wider text-sky-900 font-semibold mb-2 truncate">Edit · {series.series_name}</div>
        <Label className="text-[11px] text-muted-foreground">Sessions remaining (of {series.quantity_total})</Label>
        <Input type="number" min="0" max={series.quantity_total} value={qty} onChange={(e) => setQty(e.target.value)} className="h-8 text-sm mb-2 mt-1" data-testid={`series-qty-input-${series.id}`} />
        <Label className="text-[11px] text-muted-foreground">Expires on</Label>
        <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-8 text-sm mb-2 mt-1" data-testid={`series-expiry-input-${series.id}`} />
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="h-8 text-sm mb-3" data-testid={`series-reason-input-${series.id}`} />
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid={`series-save-btn-${series.id}`}>{busy ? "Saving…" : "Save"}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
